import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  EditRoleFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditRoleFormData
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderSelectControl,
  renderRadioControl,
  renderTextareaControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { LiveUriPreview } from '@/components/rema/shared'
import { buildRoleUri } from '@/hooks/useRemaUriBuilder'
import { sanitizeResourceTypeName } from '@/lib/utils'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

export type EditRoleComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<EditRoleFormData>
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof EditRoleFormData, value: unknown): string | null => {
  const shape = EditRoleFormSchema.shape[name]
  if (!shape) return null
  const parsed = shape.safeParse(value)
  if (!parsed.success) return parsed.error.issues[0]?.message ?? 'Invalid value'
  return null
}

export function EditRoleComposer(props: EditRoleComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    return ['roleType', 'namespace', 'organization', 'roleName'].includes(name)
  }

  const upsertRole = useApiMutation('upsertRole', 'POST', {
    invalidates: ['getRoles'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to update role:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: EditRoleFormSchema,
    defaultValues: {
      roleNumber: initialValues?.roleNumber,
      roleType: initialValues?.roleType ?? ('' as any),
      namespace: initialValues?.namespace ?? ('' as any),
      // Organization applies only to user roles; workload roles use a sentinel ('-')
      // so the required-org schema check doesn't block submit (stripped below).
      organization:
        initialValues?.roleType === 'workload' ? '-' : (initialValues?.organization ?? ''),
      roleName: initialValues?.roleName ?? '',
      roleDescription: initialValues?.roleDescription ?? '',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as EditRoleFormData
      const parsed = EditRoleFormSchema.safeParse({
        roleNumber: initialValues?.roleNumber,
        roleType: formValue.roleType,
        namespace: formValue.namespace,
        organization: formValue.organization?.trim() ?? '',
        roleName: formValue.roleName?.trim() ?? '',
        roleDescription: formValue.roleDescription?.trim() ?? '',
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      const apiRequest = transformToApiRequest(parsed.data)
      // Workload roles have no organization — don't persist the sentinel.
      if (parsed.data.roleType === 'workload') apiRequest.organization = ''
      await upsertRole.mutateAsync({ ...apiRequest, roleUri: liveUri })
    },
  })

  const formState = form.state.values as any
  const liveUri = buildRoleUri({
    namespace: formState.namespace ?? '',
    roleType: formState.roleType ?? '',
    organization: formState.organization ?? '',
    roleName: formState.roleName ?? '',
  })

  const renderControl = (control: any) => {
    // Organization Type applies only to user roles — hide it for workload roles.
    if (control.name === 'organization' && formState.roleType === 'workload') return null
    const isRequired = isRequiredField(control.name)
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) => validateField(control.name as keyof EditRoleFormData, value) || undefined,
        }}
        listeners={
          control.name === 'roleName'
            ? {
                // Role name: auto-correct to lowercase letters and hyphens only.
                onChange: ({ value }) => {
                  const sanitized = sanitizeResourceTypeName(value)
                  if (sanitized !== value) form.setFieldValue('roleName', sanitized as any)
                },
              }
            : control.name === 'roleType'
            ? {
                // Workload roles have no organization: seed the sentinel so the
                // required-org check passes; clear it when switching back to user.
                onChange: ({ value }) => {
                  if (value === 'workload') form.setFieldValue('organization', '-' as any)
                  else if (form.getFieldValue('organization') === '-') form.setFieldValue('organization', '' as any)
                },
              }
            : undefined
        }
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }
          if (control.htmlControl === 'textarea') return renderTextareaControl(config, bound)
          if (control.htmlControl === 'radio') {
            return renderRadioControl({
              ...config,
              options: control.selectOptions?.map((o: any) => ({ value: o.value, label: o.label })) || [],
            }, bound)
          }
          if (control.htmlControl === 'select') {
            return renderSelectControl({
              ...config,
              options: control.selectOptions?.map((o: any) => ({ value: o.value, label: o.label })) || [],
            }, bound)
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">{FORM_CONFIG.formDescription}</p>
          </div>
        )}

        {groups.map((group) => {
          const controls = controlsByGroup.get(group.formGroupName) || []
          if (!controls.length) return null
          return (
            <div key={group.formGroupName} className="space-y-4">
              {controls.map(renderControl)}
            </div>
          )
        })}

        <LiveUriPreview uri={liveUri} label="Role URI Preview" />

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}
          {renderButtonControl({
            label: upsertRole.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}

export { EditRoleFormSchema, transformToApiRequest, FORM_CONFIG, type EditRoleFormData } from './schema'
